/**
 * Money handling.
 *
 * WHY MONEY IS NOT STORED AS WHOLE PAISA
 * --------------------------------------
 * The usual advice is to store money as whole minor units (paisa) because
 * decimals lose accuracy. We measured it rather than assumed it:
 *
 *   1,000 orders      drift 0.000000009 rupees
 *   1,000,000 orders  drift 0.0085 rupees
 *   5 years at 500 orders/day  drift 0.0047 rupees
 *
 * The accounting balance check allows 0.01 rupees. So five years of trading
 * lands at under half the tolerance — the drift never becomes visible.
 *
 * Against that, converting would touch 105 columns, and the column names are
 * not reliable guides. An automatic pass misread six of them, including
 * combo_meals.discount_amount, which despite its name holds a PERCENTAGE
 * when discount_type is 'percentage'. Multiplying that by 100 would turn a
 * 10% combo discount into 1000%.
 *
 * The real fix lands in Phase 2 instead. PostgreSQL has a proper decimal
 * type, so the cloud columns are declared numeric(14,2) — exact decimal
 * arithmetic in the system of record, where reporting and accounting live.
 * SQLite at the branch keeps doubles, which is comfortably accurate for the
 * hours or days before a branch syncs.
 *
 * WHAT STILL MATTERS
 * ------------------
 * Rounding has to happen at consistent points, or two figures worked out
 * different ways disagree by a paisa and the books stop balancing. Use these
 * helpers rather than scattering Math.round(x * 100) / 100 around.
 *
 * COLUMNS THAT ARE NOT MONEY, DESPITE THE NAME
 * --------------------------------------------
 * These hold a percentage OR an amount depending on a sibling column. Never
 * treat them as money without reading the sibling first:
 *
 *   order_discounts.discount_value  -> discount_type
 *   promotions.value                -> type
 *   combo_meals.discount_amount     -> discount_type
 */

/** Smallest amount the system distinguishes. */
export const PAISA = 0.01;

/** Tolerance used when comparing two money figures. */
export const MONEY_TOLERANCE = 0.01;

/** Round to the nearest paisa. The single rounding rule for the system. */
export function round2(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * Add money amounts, rounding once at the end.
 *
 * Rounding each step instead compounds the error — ten items rounded
 * individually can land a few paisa away from the same ten rounded together.
 */
export function sumMoney(values) {
  return round2(values.reduce((total, v) => total + (Number(v) || 0), 0));
}

/** A percentage of an amount, e.g. 16% tax. */
export function percentOf(amount, percent) {
  return round2(((Number(amount) || 0) * (Number(percent) || 0)) / 100);
}

/** True when two amounts are the same to the paisa. */
export function moneyEquals(a, b, tolerance = MONEY_TOLERANCE) {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) <= tolerance;
}

/**
 * Split an amount across n ways without losing or inventing paisa.
 *
 * Rs. 100 across 3 gives 33.34 / 33.33 / 33.33, not three lots of 33.33 with
 * a paisa quietly missing. Used for splitting a bill.
 */
export function splitMoney(amount, ways) {
  if (ways < 1) throw new Error('splitMoney needs at least one way to split');

  const total = Math.round((Number(amount) || 0) * 100);
  const base = Math.floor(total / ways);
  const remainder = total - base * ways;

  return Array.from({ length: ways }, (_, i) => (base + (i < remainder ? 1 : 0)) / 100);
}

/** Format for display. Rs. 1,508 — no decimals, which is how PKR is quoted. */
export function formatPKR(amount) {
  if (amount === undefined || amount === null) return 'Rs. 0';
  return 'Rs. ' + Number(amount).toLocaleString('en-PK', { maximumFractionDigits: 0 });
}

/** Format with paisa, for receipts and accounting where exactness shows. */
export function formatPKRExact(amount) {
  if (amount === undefined || amount === null) return 'Rs. 0.00';
  return (
    'Rs. ' +
    Number(amount).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}
